import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";

// ============================================================================
// DEVICE MULTIPLIERS — tweak scale and position per device/orientation
// offsetX = multiplier applied to symbolWidth
// offsetY = multiplier applied to symbolHeight
// ============================================================================
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  scale: 1.0,
  offsetX: 2.25,
  offsetY: -1.0,
};

const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  scale: 1.0,
  offsetX: 3.0,
  offsetY: -0.5,
};

const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  scale: 1.0,
  offsetX: 3.0,
  offsetY: -0.5,
};

const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  scale: 1.0,
  offsetX: 3.0,
  offsetY: -0.5,
};

const DEVICE_MULTIPLIERS_DESKTOP = {
  scale: 1.0,
  offsetX: 3.0,
  offsetY: -0.5,
};

/**
 * FreeSpinCounterController
 * Manages the free spin counter spine animation.
 * Follows the same pattern as FreeSpinSceneTransitionController:
 * createSpine → warmUp → play/hide via public methods.
 * Supports per-device scaling and positioning.
 */
class FreeSpinCounterController {
  constructor(
    container,
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
    device,
    orientation,
  ) {
    this.container = container;
    this.spineData = {
      skeleton: "free_spin_counter-skeleton",
      atlas: "free_spin_counter-atlas",
    };

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.device = device;
    this.orientation = orientation;

    this.counterSpine = null;
    this.isVisible = false;

    // Text slot
    this.textContainer = null;
    this.bitmapText = null;

    this.targetScale = 1.0;
    this.calculateScale();

    this.initializeSpine();
  }

  // --- Device multipliers ---

  getDeviceMultipliers() {
    if (this.device === PixiSettings.DEVICE.MOBILE) {
      if (this.orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return DEVICE_MULTIPLIERS_MOBILE_PORTRAIT;
      }
      return DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
    }
    if (this.device === PixiSettings.DEVICE.TABLET) {
      if (this.orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return DEVICE_MULTIPLIERS_TABLET_PORTRAIT;
      }
      return DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    }
    return DEVICE_MULTIPLIERS_DESKTOP;
  }

  getSymbolScaleFactor() {
    if (!this.dimensions || !this.symbolWidth) return 1;
    return this.symbolWidth / Settings.SYMBOL_WIDTH;
  }

  calculateScale() {
    if (!this.dimensions || !this.symbolWidth || !this.symbolHeight) {
      this.targetScale = 1.0;
      return;
    }

    const symbolScaleFactor = this.getSymbolScaleFactor();
    const { scale: deviceScaleMultiplier } = this.getDeviceMultipliers();
    this.targetScale = Math.max(
      0.3,
      Math.min(1.5, symbolScaleFactor * deviceScaleMultiplier),
    );
  }

  // --- Resize ---

  resize(
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
    device,
    orientation,
  ) {
    const symbolHeightsChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;

    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensions?.width ||
      this.dimensions.height !== dimensions?.height;

    const dividerDimensionsChanged =
      !this.dividerDimensions ||
      (dividerDimensions &&
        (this.dividerDimensions.width !== dividerDimensions.width ||
          this.dividerDimensions.height !== dividerDimensions.height));

    const deviceChanged =
      this.device !== device || this.orientation !== orientation;

    if (
      !symbolHeightsChanged &&
      !dimensionsChanged &&
      !dividerDimensionsChanged &&
      !deviceChanged
    ) {
      return;
    }

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.dimensions = dimensions;
    this.dividerDimensions = dividerDimensions;
    this.device = device;
    this.orientation = orientation;

    this.calculateScale();

    if (
      this.counterSpine &&
      (symbolHeightsChanged ||
        dimensionsChanged ||
        dividerDimensionsChanged ||
        deviceChanged)
    ) {
      const { offsetX, offsetY } = this.getDeviceMultipliers();
      this.counterSpine.x = this.symbolWidth * offsetX;
      this.counterSpine.y = this.symbolHeight * offsetY;
      this.counterSpine.scale.set(this.targetScale);
    }
  }

  // --- Spine creation & warmup ---

  createSpine() {
    try {
      if (!this.spineData) return null;

      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.spineData.skeleton) ||
        !assets.has(this.spineData.atlas)
      ) {
        return null;
      }

      return Spine.from({
        skeleton: this.spineData.skeleton,
        atlas: this.spineData.atlas,
      });
    } catch (error) {
      return null;
    }
  }

  initializeSpine() {
    try {
      this.counterSpine = this.createSpine();
      if (!this.counterSpine) return;

      if (!this.counterSpine.skeleton || !this.counterSpine.state) return;

      const { offsetX, offsetY } = this.getDeviceMultipliers();
      this.counterSpine.x = this.symbolWidth * offsetX;
      this.counterSpine.y = this.symbolHeight * offsetY;
      this.counterSpine.alpha = 0.001;
      this.counterSpine.zIndex = 10;
      this.container.addChild(this.counterSpine);
      this.counterSpine.scale.set(this.targetScale);

      this.warmUp();
      this.initializeTextSlot();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Initialize text container and attach to spine slot "txt_slot_fs_counter".
   * Spine auto-handles position/rotation/scale via the slot bone.
   */
  initializeTextSlot() {
    if (!this.counterSpine || !this.counterSpine.skeleton) return;

    const slot = this.counterSpine.skeleton.findSlot("txt_slot_fs_counter");
    if (!slot) return;

    this.textContainer = new PIXI.Container();
    this.textContainer.name = "fs_counter_text";

    this.bitmapText = new PIXI.BitmapText("1/15", {
      fontName: "font",
      fontSize: 52,
    });
    this.bitmapText.anchor.set(0.5);
    this.textContainer.addChild(this.bitmapText);

    // Attach to slot — Spine auto-updates position/rotation/scale
    this.counterSpine.addSlotObject("txt_slot_fs_counter", this.textContainer);
  }

  /**
   * Update displayed text (e.g. "3/15")
   * @param {string} text - The text to display
   */
  updateText(text) {
    if (!this.bitmapText) return;
    this.bitmapText.text = text;
  }

  warmUp() {
    if (!this.counterSpine || !this.counterSpine.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.counterSpine.state.setAnimation(0, animations[0], false);
        this.counterSpine.state.update(0.016);
        this.counterSpine.skeleton.updateWorldTransform();
      }
      this.counterSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.counterSpine?.skeleton?.data?.animations) return [];
    return this.counterSpine.skeleton.data.animations.map((a) => a.name);
  }

  // --- Public API ---

  /**
   * Play counter_in → counter_idle (looping)
   */
  show() {
    if (!this.counterSpine || !this.counterSpine.state || this.isVisible)
      return;

    this.isVisible = true;
    this.counterSpine.state.clearListeners();

    // Set animation while hidden to clear warmUp pose
    this.counterSpine.state.setAnimation(0, "free_spin_counter_in", false);
    this.counterSpine.state.addAnimation(0, "free_spin_counter_idle", true, 0);

    // Show on next frame — old pose is gone, fresh animation starts
    requestAnimationFrame(() => {
      if (this.isVisible && this.counterSpine) {
        this.counterSpine.alpha = 1;
      }
    });
  }

  /**
   * Play counter_out → hide
   */
  hide() {
    if (!this.counterSpine || !this.counterSpine.state || !this.isVisible)
      return;

    this.isVisible = false;
    this.counterSpine.state.clearListeners();
    this.counterSpine.state.setAnimation(0, "free_spin_counter_out", false);

    this.counterSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "free_spin_counter_out") {
          if (this.counterSpine) {
            this.counterSpine.alpha = 0.001;
          }
        }
      },
    });
  }

  /**
   * Cleanup — destroy spine and remove from container
   */
  cleanup() {
    try {
      if (this.bitmapText && !this.bitmapText.destroyed) {
        this.bitmapText.destroy();
      }
    } catch {
      // Already destroyed
    }
    this.bitmapText = null;

    try {
      if (this.textContainer && !this.textContainer.destroyed) {
        this.textContainer.destroy({ children: true });
      }
    } catch {
      // Already destroyed
    }
    this.textContainer = null;

    if (this.counterSpine) {
      try {
        this.counterSpine.state?.clearListeners();
        this.counterSpine.state?.clearTracks();
        if (this.counterSpine.parent) {
          this.counterSpine.parent.removeChild(this.counterSpine);
        }
        this.counterSpine.destroy();
      } catch {
        // Already destroyed
      }
      this.counterSpine = null;
    }
    this.container = null;
    this.isVisible = false;
  }
}

export default FreeSpinCounterController;
